import handleErrorAndDisplay from './handleErrorAndDisplay';
import { hasDetailMessages, transformAndDisplay } from './transformErrorResponse';
import { __ } from '@wordpress/i18n';

/**
 * Display network error to user with proper error format handling
 *
 * @param {Error} error - Error object from network request
 * @param {object} options - Options object
 * @param {boolean} options.silent - If true, suppress error display
 * @returns {void}
 */
export const displayNetworkError = (error, options = {}) => {
    if (options.silent) return;

    // Silently ignore 403 errors that were handled with redirect
    if (error?.handled) {
        return;
    }

    try {
        const errorData = error?.data || error?.response?.data;

        // Priority 1: Pydantic validation errors
        if (errorData && hasDetailMessages(errorData)) {
            transformAndDisplay(handleErrorAndDisplay, errorData);
            return;
        }

        // Priority 2: String detail property
        if (errorData?.detail && typeof errorData.detail === 'string') {
            handleErrorAndDisplay(errorData.detail);
            return;
        }

        // Priority 3: Error message (skip generic 'Request failed')
        if (error?.message && error.message !== 'Request failed') {
            handleErrorAndDisplay(error.message);
            return;
        }

        // Priority 4: Status-based messages
        if (error?.status) {
            const statusMessages = {
                400: __('Richiesta non valida. Controlla i dati inseriti.', 'soundbetween'),
                401: __('Non autorizzato. Effettua di nuovo l\'accesso.', 'soundbetween'),
                403: __('Accesso negato.', 'soundbetween'),
                404: __('Risorsa non trovata.', 'soundbetween'),
                409: __('Conflitto. La risorsa esiste già.', 'soundbetween'),
                422: __('Errore di validazione. Controlla i dati inseriti.', 'soundbetween'),
                500: __('Errore del server. Riprova più tardi.', 'soundbetween'),
                502: __('Gateway non valido. Il server non è momentaneamente disponibile.', 'soundbetween'),
                503: __('Servizio non disponibile. Riprova più tardi.', 'soundbetween'),
            };

            handleErrorAndDisplay(statusMessages[error.status] ||
                __('Si è verificato un errore. Riprova.', 'soundbetween'));
            return;
        }

        // Priority 5: Generic fallback
        handleErrorAndDisplay(__('Si è verificato un errore inatteso.', 'soundbetween'));

    } catch (displayError) {
        console.error('Error displaying error:', displayError);
        handleErrorAndDisplay(__('Si è verificato un errore.', 'soundbetween'));
    }
};

export default displayNetworkError;
